/**
 * Transport local : un appareil, plusieurs onglets.
 *
 * C'est le mode « bureau unique », utilisable sans réseau ni serveur :
 * - le bureau de l'enseignant vit dans un onglet,
 * - l'affichage vidéoprojeté dans un second onglet (autre écran),
 * - l'isoloir dans un troisième.
 *
 * Chaque instance émettrice écrit dans **sa propre clé** de stockage : deux
 * onglets n'écrivent donc jamais au même endroit, et la fusion des journaux
 * reste sans conflit, exactement comme le prévoit la spécification.
 */

import { mergeOrder, type JournalEvent } from '../core/index.js';
import type { Transport } from './transport.js';

const PREFIX = 'election.nsi.xyz/v1';
const INDEX_KEY = `${PREFIX}/index`;

export interface ElectionSummary {
  readonly id: string;
  readonly nom: string;
  readonly classe: string;
  readonly etablissement: string;
  readonly dateISO: string;
  readonly creeLe: number;
}

function lireIndex(): ElectionSummary[] {
  try {
    const brut = localStorage.getItem(INDEX_KEY);
    if (!brut) return [];
    const parse = JSON.parse(brut) as unknown;
    return Array.isArray(parse) ? (parse as ElectionSummary[]) : [];
  } catch {
    return [];
  }
}

export function listElections(): ElectionSummary[] {
  return lireIndex().sort((a, b) => b.creeLe - a.creeLe);
}

export function rememberElection(resume: ElectionSummary): void {
  const index = lireIndex().filter((entry) => entry.id !== resume.id);
  index.push(resume);
  localStorage.setItem(INDEX_KEY, JSON.stringify(index));
}

function forgetElection(id: string): void {
  localStorage.setItem(INDEX_KEY, JSON.stringify(lireIndex().filter((entry) => entry.id !== id)));
}

/** Identifiant d'instance, stable pour l'onglet courant (session). */
export function originFor(role: string): string {
  const key = `${PREFIX}/origin/${role}`;
  const existant = sessionStorage.getItem(key);
  if (existant) return existant;
  const octets = new Uint8Array(4);
  crypto.getRandomValues(octets);
  const id = `${role === 'bureau' ? 'T' : role === 'isoloir' ? 'I' : 'D'}_${Array.from(octets, (octet) => octet.toString(16).padStart(2, '0')).join('')}`;
  sessionStorage.setItem(key, id);
  return id;
}

/** Journal local d'une instance, relu depuis le stockage. */
export class LocalTransport implements Transport {
  readonly kind = 'local';
  private readonly prefixe: string;
  private readonly channel: BroadcastChannel | null;

  constructor(readonly electionId: string) {
    this.prefixe = `${PREFIX}/${electionId}/`;
    this.channel = typeof BroadcastChannel === 'function' ? new BroadcastChannel(`election.nsi.xyz/${electionId}`) : null;
  }

  private cle(origin: string): string {
    return `${this.prefixe}${origin}`;
  }

  async list(): Promise<JournalEvent[]> {
    const evenements: JournalEvent[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const cle = localStorage.key(i);
      if (cle === null || !cle.startsWith(this.prefixe)) continue;
      try {
        const parse = JSON.parse(localStorage.getItem(cle) ?? '[]') as unknown;
        if (Array.isArray(parse)) evenements.push(...(parse as JournalEvent[]));
      } catch {
        // Une clé corrompue ne doit jamais bloquer le dépouillement : elle est ignorée.
      }
    }
    return mergeOrder(evenements);
  }

  async append(event: JournalEvent): Promise<void> {
    const cle = this.cle(event.origin);
    let liste: JournalEvent[] = [];
    try {
      const parse = JSON.parse(localStorage.getItem(cle) ?? '[]') as unknown;
      if (Array.isArray(parse)) liste = parse as JournalEvent[];
    } catch {
      liste = [];
    }
    liste.push(event);
    localStorage.setItem(cle, JSON.stringify(liste));
    this.channel?.postMessage({ type: 'changed' });
  }

  subscribe(listener: () => void): () => void {
    const surMessage = (): void => listener();
    const surStockage = (evenement: StorageEvent): void => {
      if (evenement.key === null || evenement.key.startsWith(this.prefixe)) listener();
    };
    this.channel?.addEventListener('message', surMessage);
    window.addEventListener('storage', surStockage);
    return () => {
      this.channel?.removeEventListener('message', surMessage);
      window.removeEventListener('storage', surStockage);
    };
  }

  async clear(): Promise<void> {
    const aSupprimer: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const cle = localStorage.key(i);
      if (cle !== null && cle.startsWith(this.prefixe)) aSupprimer.push(cle);
    }
    for (const cle of aSupprimer) localStorage.removeItem(cle);
    localStorage.removeItem(codesKey(this.electionId));
    forgetElection(this.electionId);
    this.channel?.postMessage({ type: 'changed' });
  }

  /**
   * Restaure un journal complet, et la planche de codes si elle est fournie.
   *
   * Les événements sont réécrits **par instance émettrice**, exactement comme
   * en fonctionnement normal : la chaîne d'empreintes de chaque instance est
   * donc préservée à l'identique, et le journal restauré est bit pour bit
   * celui qui avait été exporté.
   */
  async restore(events: readonly JournalEvent[], codes: StoredCodes | null): Promise<void> {
    await this.clear();
    const parOrigine = new Map<string, JournalEvent[]>();
    for (const evenement of events) {
      const liste = parOrigine.get(evenement.origin) ?? [];
      liste.push(evenement);
      parOrigine.set(evenement.origin, liste);
    }
    for (const [origine, liste] of parOrigine) {
      localStorage.setItem(`${this.prefixe}${origine}`, JSON.stringify(liste));
    }
    if (codes !== null) saveCodes(this.electionId, codes);
    this.channel?.postMessage({ type: 'changed' });
  }
}

/**
 * Codes en clair : ils ne vivent **jamais** dans le journal, seulement sur
 * l'appareil de l'enseignant et sur le papier distribué (§7.2).
 */
export interface StoredCodes {
  readonly eleves: readonly { raw: string; display: string }[];
  readonly enseignant: { raw: string; display: string };
}

export function codesKey(electionId: string): string {
  return `${PREFIX}/codes/${electionId}`;
}

export function saveCodes(electionId: string, codes: StoredCodes): void {
  localStorage.setItem(codesKey(electionId), JSON.stringify(codes));
}

export function loadCodes(electionId: string): StoredCodes | null {
  try {
    const brut = localStorage.getItem(codesKey(electionId));
    if (!brut) return null;
    return JSON.parse(brut) as StoredCodes;
  } catch {
    return null;
  }
}

/** Purge locale : efface tout ce qui concerne une élection (D13). */
export function purgeElection(electionId: string): void {
  const prefixe = `${PREFIX}/${electionId}/`;
  const aSupprimer: string[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const cle = localStorage.key(i);
    if (cle !== null && cle.startsWith(prefixe)) aSupprimer.push(cle);
  }
  for (const cle of aSupprimer) localStorage.removeItem(cle);
  localStorage.removeItem(codesKey(electionId));
  forgetElection(electionId);
}
